import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { useCurrentCompany } from 'api/authentication/hooks';
import { useWebSocket } from 'api/hooks';
import { handlePossibleNonFieldError } from 'api/query-client';

import {
  DuplicateAIAgentData,
  createAIAgent,
  duplicateAIAgent,
  generateAIAgentOutputFromInstructions,
  loadAIAgent,
  loadAIAgents,
  loadHiddenModels,
  removeAIAgent,
  testAIAgent,
  updateAIAgent,
} from './requests';

const aiAgentQueryKey = ['ai-agents'];

const useInvalidateAIAgents = () => {
  const queryClient = useQueryClient();
  const invalidateAIAgents = () => queryClient.invalidateQueries(aiAgentQueryKey);

  return { invalidateAIAgents };
};

export const useAIAgentTemplateUpdates = () => {
  const company = useCurrentCompany();
  const { invalidateAIAgents } = useInvalidateAIAgents();

  useWebSocket(`ai-agents/${company?.shortId}/`, invalidateAIAgents, {
    enabled: Boolean(company?.shortId),
    throttleTime: 0,
  });
};

export const useAIAgents = (
  options: {
    queryString?: string;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
  } = {}
) => {
  const { queryString = '', ...rest } = options;
  return useQuery({
    queryKey: ['ai-agents', queryString],
    queryFn: () => loadAIAgents(queryString),
    keepPreviousData: true,
    ...rest,
  });
};

export const useAIAgent = (shortId: string, options: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ['ai-agent', shortId],
    queryFn: () => {
      return loadAIAgent(shortId);
    },
    enabled: Boolean(shortId) && options.enabled !== false,
  });
};

export const useCreateAIAgent = () => {
  const { invalidateAIAgents } = useInvalidateAIAgents();

  return useMutation({
    mutationKey: ['ai-agent-create'],
    mutationFn: createAIAgent,
    onSuccess: () => {
      invalidateAIAgents();
    },
  });
};

export const useUpdateAIAgent = () => {
  const { invalidateAIAgents } = useInvalidateAIAgents();

  return useMutation({
    mutationKey: ['ai-agent-update'],
    mutationFn: updateAIAgent,
    onSuccess: () => {
      invalidateAIAgents();
    },
  });
};

export const useDuplicateAIAgent = (shortId: string) => {
  const { invalidateAIAgents } = useInvalidateAIAgents();
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationKey: ['ai-agent-duplicate'],
    mutationFn: (data: DuplicateAIAgentData) => duplicateAIAgent(shortId, data),
    onSuccess: () => {
      invalidateAIAgents();
      showSuccess(t('agentDuplicated'));
    },
  });
};

export const useRemoveAIAgent = () => {
  const { invalidateAIAgents } = useInvalidateAIAgents();
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationKey: ['ai-agent-remove'],
    mutationFn: removeAIAgent,
    onSuccess: () => {
      invalidateAIAgents();
      showSuccess(t('agentRemoved'));
    },
  });
};

export const useTestAIAgent = () => {
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationKey: ['ai-agent-test'],
    mutationFn: testAIAgent,
    onError: (error) => handlePossibleNonFieldError(error, t('testFailed')),
  });
};

const locale = {
  en: {
    agentDuplicated: 'Agent duplicated',
    agentRemoved: 'Agent removed',
    testFailed: 'Could not run the test',
  },
};

export const useHiddenModels = () => {
  return useQuery({
    queryKey: ['ai-hidden-models'],
    queryFn: loadHiddenModels,
  });
};

export const useGenerateAIAgentOutputFromInstructions = () => {
  return useMutation({
    mutationKey: ['ai-agent-generate-output-from-instructions'],
    mutationFn: generateAIAgentOutputFromInstructions,
  });
};
